import React from "react";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { Td } from "../../../components/BaseUI/Table";
import { formatInstantDateShortTr } from "../../../utils/appTime";
import ApprovalResultBadge from "../../ApprovalResultBadge";
import OrderApprovalDetail from "./OrderApprovalDetail";
import OrderDetailHeader from "./OrderDetailHeader";
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import {
  resolveOrderFromItem,
  resolveCompanyNameFromItem,
  parseRateNum,
  formatRateDisplay,
} from "./orderDetailUtils";
import { limitWords } from "../../../utils/textDisplay";
import {
  adminApproveOrder,
  adminRejectOrder,
  userApproveOrder,
  userRejectOrder,
} from "./api";

const getEntity = (item) => item?.entity ?? item;
/** API calls use business entity id, not ApprovalRecord id. */
const getEntityId = (item) =>
  item?.entityId ?? getEntity(item)?._id ?? item?.id;

const renderOrderCells = (item, { format, visibleColumns, fx }, history = false) => {
  const show = (id) => !visibleColumns || visibleColumns.includes(id);
  const companyName = resolveCompanyNameFromItem(item);
  const rate = item.summary?.exchangeRate ?? item.entity?.exchangeRate;
  const rateNum = parseRateNum(rate);
  const isFallback = !rateNum && !!fx?.rate;
  const displayRate = rateNum ?? parseRateNum(fx?.rate);

  const date = history
    ? item.status === "pending"
      ? item.requestedAt
      : item.resolvedAt
    : item.requestedAt;

  const actor = history
    ? item.status === "pending"
      ? item.createdBy?.name || item.requestedBy?.name || "-"
      : item.resolvedBy?.name || item.createdBy?.name || "-"
    : item.createdBy?.name || item.requestedBy?.name || "-";

  return (
    <>
      {show(0) && (
        <Td>
          <Lozenge appearance="info-subtle">{item.typeLabel}</Lozenge>
        </Td>
      )}
      {show(1) && (
        <Td>
          <span className="fw-semibold" title={item.title || undefined}>
            {limitWords(item.title)}
          </span>
        </Td>
      )}
      {show(2) && (
        <Td>
          <span title={companyName !== "-" ? companyName : undefined}>
            {limitWords(companyName === "-" ? "" : companyName)}
          </span>
        </Td>
      )}
      {show(3) && (
        <Td>
          <span className="fw-semibold text-primary">
            <ProductPrice
              value={item.summary?.totalAmount || 0}
              rate={rateNum}
              showFallbackMark={isFallback}
            />
          </span>
        </Td>
      )}
      {show(9) && (
        <Td>
          {displayRate ? (
            <span>
              {formatRateDisplay(displayRate)}
              {isFallback && (
                <span className="text-warning fw-bold ms-1" title="Güncel kur">
                  *
                </span>
              )}
            </span>
          ) : (
            "-"
          )}
        </Td>
      )}
      {show(4) && (
        <Td>
          <ApprovalResultBadge item={item} />
        </Td>
      )}
      {show(5) && <Td>{actor}</Td>}
      {show(6) && <Td>{item.previousStepApprovedBy?.name || "-"}</Td>}
      {show(7) && <Td>{date ? formatInstantDateShortTr(date) : "-"}</Td>}
    </>
  );
};

export const orderApprovalPlugin = {
  type: "order",
  typeLabel: "Teklif",

  getId: getEntityId,
  getEntity,
  getTitle: (item) => item?.title ?? getEntity(item)?.orderName ?? "",

  stages: {
    user: {
      canActOn: (item) =>
        (item?.currentApproval?.stage === "user" &&
          item?.currentApproval?.status !== "approved") ||
        getEntity(item)?.status === "admin1_approved",
      approve: (item, { token, attachment }) =>
        userApproveOrder(token, getEntityId(item), attachment),
      reject: (item, reason, { token }) =>
        userRejectOrder(token, getEntityId(item), reason),
      messages: {
        approveSuccess: "Müşteri onayı kaydedildi — sipariş onayına gönderildi",
        rejectSuccess: "Teklif reddedildi",
      },
      approveLabel: "Sipariş Onayı",
      rejectLabel: "Reddet",
      rejectModalTitle: "Teklifi Reddet",
    },
    admin: {
      canActOn: (item) => item?.status === "pending",
      approve: (item, { token, attachment }) =>
        adminApproveOrder(token, getEntityId(item), attachment),
      reject: (item, reason, { token }) =>
        adminRejectOrder(token, getEntityId(item), reason),
      messages: {
        approveSuccess: "Onaylandı — Sonraki adıma gönderildi",
        rejectSuccess: "Teklif reddedildi",
      },
      approveLabel: "Onayla",
      rejectLabel: "Reddet",
      rejectModalTitle: "Teklifi Reddet (Yönetici)",
      detailTitle: "Teklif Detayı",
    },
  },

  inbox: {
    detailWidth: "x-large",
    detailScrollBehavior: "inside",
    renderDetailHeader: (item) => (
      <OrderDetailHeader
        orderName={resolveOrderFromItem(item)?.orderName || item?.title}
      />
    ),
    renderDetailBody: (item, { format, navigate, detailSection }) => (
      <OrderApprovalDetail
        item={item}
        format={format}
        navigate={navigate}
        detailSection={detailSection}
      />
    ),
    renderRowCells: (item, context) => renderOrderCells(item, context, false),
    renderHistoryCells: (item, context) => renderOrderCells(item, context, true),
  },
};

export default orderApprovalPlugin;
